import { LocationState, LocationStatus, useLocationState } from '@/hooks/use-location-state';
import { Coords } from '@/utils/location-source';
import { DefaultLocation } from '@/utils/location-defaults';
import React, { createContext, useContext } from 'react';

export type { LocationStatus };

/** What a screen needs to decide whether -- and where -- to show nearby content. */
export interface UserLocation {
  status: LocationStatus;
  coords: Coords | null;
  defaultLocation: DefaultLocation;
}

const LocationContext = createContext<LocationState | undefined>(undefined);

/**
 * Shares one location acquisition across the app.
 *
 * Mount inside `AppGateProvider`: the engine waits for the gate to reach `'app'`
 * before touching the OS, so the location prompt never surfaces on the login
 * screen or over a setup step.
 */
export function LocationProvider({ children }: { children: React.ReactNode }) {
  const value = useLocationState();

  return <LocationContext.Provider value={value}>{children}</LocationContext.Provider>;
}

function useLocationContext(hookName: string): LocationState {
  const context = useContext(LocationContext);
  if (!context) {
    throw new Error(`${hookName} must be used within LocationProvider`);
  }
  return context;
}

/**
 * The user's location and why it is or isn't available.
 *
 * Screens that only need a map centre can fall back to `defaultLocation` when
 * `coords` is null. Screens that make a claim about the user's surroundings (the
 * Nearby feed) should gate on `status` instead -- a silent fallback there would
 * present far-away content as nearby.
 */
export function useUserLocation(): UserLocation {
  const { status, coords, defaultLocation } = useLocationContext('useUserLocation');
  return { status, coords, defaultLocation };
}

/** Re-acquire the location on demand, e.g. from a retry button. */
export function useLocationControl() {
  const { refresh, isRefreshing } = useLocationContext('useLocationControl');
  return { refresh, isRefreshing };
}
